import React from 'react';
import styled, { css, DefaultTheme, FlattenSimpleInterpolation } from 'styled-components';
import { MarginProps } from 'styled-system';

import { Margin } from 'src/packages/core';
import { ThemeType, LightTheme, DarkTheme } from 'src/packages/theme';
import { hexToRGBA } from 'src/utils/hex-to-rgba';

export const getColorStyles = (
  errorMessage: string | undefined,
  readOnly: boolean,
  theme: DefaultTheme
) => {
  if (errorMessage) {
    return css`
      color: ${theme.colors.text.primary};
      border-color: ${theme.colors.palette.red} !important;
      background-color: transparent !important;
    `;
  }

  if (readOnly) {
    return css`
      color: ${theme.colors.text.primary};
      border-color: ${theme.colors.border.lighter} !important;
      background-color: ${theme.colors.background.secondary.BG4} !important;
      user-select: none;
    `;
  }

  return css`
    color: ${theme.colors.text.primary};
    border-color: ${theme.colors.border.middle};
    background-color: transparent !important;
  `;
};

export const StyledTextareaContainer = styled((props) => <Margin {...props} />)<
  { width?: string } & MarginProps
>`
  position: relative;
  height: max-content;
  display: flex;
  flex-direction: column;
  width: ${(props) => props.width ?? 'fit-content'};
  gap: 4px;
  align-items: ${(props) => (props.theme.rtl ? 'flex-end' : 'flex-start')};
`;

export const TextareaWrapper = styled.div<{ defaulttheme: DefaultTheme }>`
  position: relative;
  width: 100%;
`;

export const StyledTextarea = styled.textarea<{
  colorStyles?: FlattenSimpleInterpolation;
  defaulttheme: DefaultTheme;
  themetype?: ThemeType;
  readOnly: boolean;
}>`
  border-width: 1px;
  border-style: solid;
  border-color: ${({ defaulttheme }) => defaulttheme.colors.border.middle};
  border-radius: 6px;
  outline: 0;
  width: 100%;
  margin: 0;
  display: flex;
  align-items: center;
  resize: vertical;
  padding: 12px;
  ${(props) => props.colorStyles};
  direction: ltr;

  :hover,
  :focus {
    border-color: ${({ defaulttheme }) => defaulttheme.colors.palette.primary};
  }

  &,
  ::placeholder {
    font-size: ${({ theme }) => theme.typographyScale.desktop.p1.fontSize};
    font-weight: ${({ theme }) => theme.typographyScale.desktop.p1.fontWeight};
    line-height: ${({ theme }) => theme.typographyScale.desktop.p1.lineHeight};
    font-family: ${({ theme }) => theme.typographyScale.desktop.p1.fontFamily};
  }

  ::placeholder {
    color: ${({ defaulttheme }) => defaulttheme.colors.text.tertiary};
  }

  :disabled {
    cursor: not-allowed;
    color: ${({ defaulttheme }) => defaulttheme.colors.text.tertiary} !important;
    border: unset !important;
    background-color: ${({ themetype }) =>
      themetype && themetype === 'light'
        ? hexToRGBA(DarkTheme.colors.background.secondary.BG1, 0.08)
        : hexToRGBA(LightTheme.colors.background.secondary.BG1, 0.08)} !important;
  }

  :disabled::placeholder {
    color: ${({ defaulttheme }) => defaulttheme.colors.text.tertiary};
  }

  ${({ theme }) => theme.mediaQueries.md} {
    &,
    ::placeholder {
      font-size: ${({ theme }) => theme.typographyScale.mobile.p1.fontSize};
      font-weight: ${({ theme }) => theme.typographyScale.mobile.p1.fontWeight};
      line-height: ${({ theme }) => theme.typographyScale.mobile.p1.lineHeight};
      font-family: ${({ theme }) => theme.typographyScale.mobile.p1.fontFamily};
    }
  }
`;
